/**
 * Hot Bills (session) / Ballot measures (elections) — the second working
 * panel. Every row is real record data: status chip, title, and the bill's
 * next scheduled event. "Hot" ordering is computed: hearing within 48h, then
 * a change in the last 24h, then the rest. Notes are authored through the
 * page-level New note button, not per row.
 */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Calendar, Flame, Vote } from 'lucide-react';
import { SpinnerIcon } from '../../icons';
import type { BillsStatusPayload, HotBill, TrackedMeasure } from './useProHomeData';
import type { HomeMode } from './useHomeMode';
import {
  classifyBillStatus,
  formatEventTime,
  parseDate,
  rankHotBills,
  type StatusTone,
} from './homeFormat';

/** A measure's short name: popular name, then the SOS unofficial caption, then the full ballot title. */
export function measureShortName(measure: Pick<TrackedMeasure, 'popular_name' | 'unofficial_caption' | 'ballot_title'>): string {
  return measure.popular_name || measure.unofficial_caption || measure.ballot_title;
}

const CHIP_TONE_CLASSES: Record<StatusTone, string> = {
  red: 'bg-party-republican-bg text-party-republican',
  green: 'bg-success-100 text-success-700',
  amber: 'bg-warning-100 text-warning-700',
  neutral: 'bg-neutral-100 text-slate-600',
};

function StatusChip({ label, tone }: { label: string; tone: StatusTone }) {
  return (
    <span
      className={`inline-flex items-center rounded-[5px] px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-[.06em] max-w-[180px] truncate ${CHIP_TONE_CLASSES[tone]}`}
      title={label}
    >
      {label}
    </span>
  );
}

interface HotBillsPanelProps {
  mode: HomeMode;
  bills: BillsStatusPayload | undefined;
  billsLoading: boolean;
  measures: TrackedMeasure[] | undefined;
  measuresLoading: boolean;
}

const HotBillsPanel: React.FC<HotBillsPanelProps> = ({
  mode,
  bills,
  billsLoading,
  measures,
  measuresLoading,
}) => {
  const rankedBills = useMemo(() => rankHotBills(bills?.hotBills ?? []), [bills]);

  const isElections = mode === 'elections';
  const isLoading = isElections ? measuresLoading : billsLoading;

  const hotCount = rankedBills.length;
  const totalTracked = bills?.totalTrackedAll ?? bills?.totalTracked ?? 0;
  const workspaceCount = bills?.trackedWorkspaceCount ?? 0;

  return (
    <section
      className="card p-0 flex flex-col overflow-hidden min-h-0 max-h-[520px] xl:max-h-none"
      aria-label={isElections ? 'Ballot measures' : 'Hot Bills'}
      data-testid="dashboard-bills-status"
    >
      <div className="flex-none border-b border-border-light dark:border-border-dark-hairline px-4 py-3.5 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-display font-semibold text-[15px] text-primary-800 dark:text-text-dark-primary">
          {isElections ? (
            <Vote className="w-4 h-4 text-action" aria-hidden="true" />
          ) : (
            <Flame className="w-4 h-4 text-action" aria-hidden="true" />
          )}
          {isElections ? 'Ballot measures' : 'Hot Bills'}
        </h2>
        <Link
          to={isElections ? '/ballot-initiatives' : '/legislation?hot=true'}
          className="text-xs font-semibold text-primary-600 dark:text-primary-dark-500 hover:text-primary-700"
        >
          View all →
        </Link>
      </div>

      <div className="pro-home-scroll flex-1 min-h-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <SpinnerIcon className="w-5 h-5 animate-spin text-primary-400" aria-hidden="true" />
          </div>
        ) : isElections ? (
          (measures ?? []).length === 0 ? (
            <p className="px-4 py-10 text-sm text-slate-500 dark:text-text-dark-secondary text-center">
              No ballot measures on your board yet.{' '}
              <Link to="/ballot-initiatives" className="font-semibold text-primary-600 hover:text-primary-700">
                Browse measures
              </Link>
            </p>
          ) : (
            (measures ?? []).map((measure) => (
              <div
                key={measure.id}
                className="px-4 py-3 border-b border-border-light dark:border-border-dark-hairline flex flex-col gap-1.5"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {measure.measure_number && (
                    <Link
                      to={`/ballot-initiatives/${measure.id}`}
                      className="text-[12.5px] font-bold text-primary-700 dark:text-primary-dark-500 flex-none hover:text-primary-600 hover:underline"
                    >
                      {measure.measure_number}
                    </Link>
                  )}
                  {measure.status && (
                    <StatusChip label={measure.status.replace(/_/g, ' ')} tone="neutral" />
                  )}
                  {measure.workspaceIds[0] ? (
                    <Link
                      to={`/workspaces/${measure.workspaceIds[0]}`}
                      title={measure.workspaceNames.join(', ')}
                      className="ml-auto text-[11px] text-slate-400 dark:text-text-dark-tertiary truncate hover:text-primary-600 hover:underline"
                    >
                      {measure.workspaceNames[0]}
                    </Link>
                  ) : (
                    <span className="ml-auto text-[11px] text-slate-400 dark:text-text-dark-tertiary truncate">
                      {measure.workspaceNames[0]}
                    </span>
                  )}
                </div>
                <Link
                  to={`/ballot-initiatives/${measure.id}`}
                  className="text-[13px] font-semibold text-primary-800 dark:text-text-dark-primary hover:text-primary-600"
                >
                  {measureShortName(measure)}
                </Link>
              </div>
            ))
          )
        ) : rankedBills.length === 0 ? (
          <p className="px-4 py-10 text-sm text-slate-500 dark:text-text-dark-secondary text-center">
            {totalTracked > 0
              ? `Nothing needs attention across your ${totalTracked} tracked bills.`
              : 'Track your first bill to see it here.'}{' '}
            <Link to="/legislation" className="font-semibold text-primary-600 hover:text-primary-700">
              Open the full list
            </Link>
          </p>
        ) : (
          rankedBills.map((bill: HotBill) => {
            const event = bill.nextCalendarEvent;
            return (
              <div
                key={bill.id}
                className="px-4 py-3 border-b border-border-light dark:border-border-dark-hairline flex flex-col gap-1.5"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Link
                    to={bill.detailUrl}
                    className="text-[12.5px] font-bold text-primary-700 dark:text-primary-dark-500 flex-none hover:text-primary-600 hover:underline"
                  >
                    {bill.billNumber}
                  </Link>
                  <StatusChip label={bill.status} tone={classifyBillStatus(bill.status)} />
                  {bill.workspaceIds[0] ? (
                    <Link
                      to={`/workspaces/${bill.workspaceIds[0]}`}
                      title={bill.workspaceNames.join(', ')}
                      className="ml-auto text-[11px] text-slate-400 dark:text-text-dark-tertiary truncate flex-none max-w-[140px] hover:text-primary-600 hover:underline"
                    >
                      {bill.workspaceNames.length > 1
                        ? `${bill.workspaceName} +${bill.workspaceNames.length - 1}`
                        : bill.workspaceName}
                    </Link>
                  ) : (
                    <span className="ml-auto text-[11px] text-slate-400 dark:text-text-dark-tertiary truncate flex-none max-w-[140px]">
                      {bill.workspaceName}
                    </span>
                  )}
                </div>
                <Link
                  to={bill.detailUrl}
                  className="text-[13px] font-semibold text-primary-800 dark:text-text-dark-primary hover:text-primary-600"
                >
                  {bill.title}
                </Link>
                <span
                  className={`flex items-center gap-1.5 text-[12px] truncate ${
                    event
                      ? 'text-primary-700 dark:text-text-dark-primary'
                      : 'text-slate-400 dark:text-text-dark-tertiary'
                  }`}
                >
                  <Calendar className="w-3.5 h-3.5 flex-none" aria-hidden="true" />
                  {event
                    ? `${format(parseDate(event.reading_date), 'EEE M/d')} · ${formatEventTime(event)} · ${
                        event.derived_location || event.chamber
                      }`
                    : 'No hearings scheduled'}
                </span>
              </div>
            );
          })
        )}
      </div>

      <div className="flex-none border-t border-border-light dark:border-border-dark-hairline bg-neutral-50 dark:bg-background-dark-tertiary px-4 py-2.5 flex items-center justify-between">
        <span className="text-[11.5px] text-slate-500 dark:text-text-dark-secondary">
          {isElections
            ? `${measures?.length ?? 0} measure${(measures?.length ?? 0) === 1 ? '' : 's'} on your board`
            : workspaceCount > 0
              ? `${hotCount} hot · ${totalTracked} tracked across ${workspaceCount} workspace${workspaceCount === 1 ? '' : 's'}`
              : `${hotCount} hot bill${hotCount === 1 ? '' : 's'}`}
        </span>
        <Link
          to={isElections ? '/ballot-initiatives' : '/legislation?hot=true'}
          className="text-xs font-semibold text-primary-600 dark:text-primary-dark-500 hover:text-primary-700"
        >
          Open the full list →
        </Link>
      </div>
    </section>
  );
};

export default HotBillsPanel;
